import { Accordion } from '@chakra-ui/react';
import { useCallback } from 'react';
import {
  FILTER_ROOMS,
  FILTER_PRICE,
  FILTER_MONTHLY_FEE,
  FILTER_LIVING_SPACE,
  FILTER_PLOT_SIZE,
  FILTER_BUILD_YEAR,
  FILTER_BEDROOMS,
} from '~/constants';
import {
  getBedrooms,
  getBuildYear,
  getLivingSpace,
  getPlotSize,
  getRooms,
} from '@habity/services';
import { RangeFilter } from './RangeFilter';
import { AccordionTabItem } from '../../tabs/AccordionTabItem';
import { Features } from './Features';
import { Statuses } from './Statuses';
import { useFilters } from '~/hooks/useFilters';
import { Nearby } from './Nearby';
import { FEATURES, NEARBY } from '@habity/constants';
import { useHomesStore } from '~/store/useHomesStore';
import { Categories } from './Categories';
import { useUser } from '~/hooks/useUser';
import { formatCurrency } from '@habity/utils';
import { useTranslations } from 'next-intl';
import { CATEGORIES } from '@habity/constants';

interface FiltersProps {
  hidePrice?: boolean;
  hideBuildYear?: boolean;
  hideBedrooms?: boolean;
  hideMonthlyFee?: boolean;
  hideNearby?: boolean;
  hideFeatures?: boolean;
  hideStatuses?: boolean;
  hideCategories?: boolean;
  ignoreSearch?: boolean;
}

// Good luck maintaining this
export function Filters({
  hidePrice = false,
  hideBuildYear = false,
  hideBedrooms = false,
  hideMonthlyFee = false,
  hideNearby = false,
  hideFeatures = false,
  hideStatuses = false,
  hideCategories = false,
  ignoreSearch,
}: FiltersProps) {
  const filters = useHomesStore((state) => state.filters);

  const { performSearch } = useFilters();
  const t = useTranslations('Status');

  const { user } = useUser();
  const { features, statuses, nearby, categories } = filters || {};

  const handleChangeEnd = useCallback(() => {
    if (!ignoreSearch) {
      const currentFilters = useHomesStore.getState().filters;
      performSearch({ filters: currentFilters });
    }
  }, [ignoreSearch, performSearch]);

  const getFeatures = () => {
    return features && features.length > 0
      ? features
          .map((n) => {
            const title = FEATURES.find((f) => f.key === n)?.title;
            return title ? title : n;
          })
          .join(', ')
      : '';
  };

  const getStatuses = () => {
    return statuses && statuses.length > 0
      ? statuses
          .map((s) => {
            const title = t(`${s}.title`);
            return title ? title : s;
          })
          .join(', ')
      : '';
  };

  const getNearby = () => {
    return nearby && nearby.length > 0
      ? nearby
          .map((n) => {
            const title = NEARBY.find((b) => b.key === n)?.title;
            return title ? title : n;
          })
          .join(', ')
      : '';
  };

  const getCategories = () => {
    if (!categories || categories.length === 0) return '';
    const labels = categories.map((n) => {
      const title = CATEGORIES.find((c) => c.values.includes(n))?.label;
      return title ? title : n;
    });
    return labels
      .filter((element, index) => {
        return labels.indexOf(element) === index;
      })
      .join(', ');
  };

  return (
    <Accordion allowToggle>
      {!hideCategories && (
        <AccordionTabItem
          title="Bostadstyp"
          subtitle={getCategories()}
          panelProps={{ py: '2' }}
        >
          <Categories hideTitle ignoreSearch={ignoreSearch} />
        </AccordionTabItem>
      )}

      {!hidePrice && (
        <AccordionTabItem
          title="Prisbild"
          subtitle={`(${formatCurrency(
            filters?.price_min || FILTER_PRICE.MIN
          )} - ${formatCurrency(filters?.price_max || FILTER_PRICE.MAX)}${
            !filters?.price_max ? ' +' : ''
          })`}
        >
          <RangeFilter
            title="price"
            value={[
              filters?.price_min || FILTER_PRICE.MIN,
              filters?.price_max || FILTER_PRICE.MAX,
            ]}
            min={FILTER_PRICE.MIN}
            max={FILTER_PRICE.MAX}
            step={FILTER_PRICE.STEP}
            onChange={(value) => {
              performSearch({
                filters: {
                  price_min: value[0] <= FILTER_PRICE.MIN ? null : value[0],
                  price_max: value[1] >= FILTER_PRICE.MAX ? null : value[1],
                },
                ignoreSearch: true,
              });
            }}
            onChangeEnd={handleChangeEnd}
            minLabel={formatCurrency(filters?.price_min || FILTER_PRICE.MIN)}
            maxLabel={formatCurrency(filters?.price_max || FILTER_PRICE.MAX)}
          />
        </AccordionTabItem>
      )}

      <AccordionTabItem
        title="Rum"
        subtitle={`(${getRooms(
          filters?.rooms_min || FILTER_ROOMS.MIN
        )} - ${getRooms(filters?.rooms_max || FILTER_ROOMS.MAX)}${
          !filters?.rooms_max ? ' eller fler' : ''
        })`}
      >
        <RangeFilter
          title="rooms"
          min={FILTER_ROOMS.MIN}
          max={FILTER_ROOMS.MAX}
          step={FILTER_ROOMS.STEP}
          value={[
            filters?.rooms_min ?? FILTER_ROOMS.MIN,
            filters?.rooms_max ?? FILTER_ROOMS.MAX,
          ]}
          onChange={(value) =>
            performSearch({
              filters: {
                rooms_min: value[0] <= FILTER_ROOMS.MIN ? null : value[0],
                rooms_max: value[1] >= FILTER_ROOMS.MAX ? null : value[1],
              },
              ignoreSearch: true,
            })
          }
          onChangeEnd={handleChangeEnd}
          minLabel={`${FILTER_ROOMS.MIN} rum`}
          maxLabel={`${FILTER_ROOMS.MAX} rum`}
        />
      </AccordionTabItem>

      {!hideBedrooms && (
        <AccordionTabItem
          title="Sovrum"
          subtitle={`(${getBedrooms(
            filters?.bedrooms_min || FILTER_BEDROOMS.MIN
          )} - ${getBedrooms(filters?.bedrooms_max || FILTER_BEDROOMS.MAX)}${
            !filters?.bedrooms_max ? ' eller fler' : ''
          })`}
        >
          <RangeFilter
            title="bedrooms"
            min={FILTER_BEDROOMS.MIN}
            max={FILTER_BEDROOMS.MAX}
            step={FILTER_BEDROOMS.STEP}
            value={[
              filters?.bedrooms_min || FILTER_BEDROOMS.MIN,
              filters?.bedrooms_max || FILTER_BEDROOMS.MAX,
            ]}
            onChange={(value) =>
              performSearch({
                filters: {
                  bedrooms_min:
                    value[0] <= FILTER_BEDROOMS.MIN ? null : value[0],
                  bedrooms_max:
                    value[1] >= FILTER_BEDROOMS.MAX ? null : value[1],
                },
                ignoreSearch: true,
              })
            }
            onChangeEnd={handleChangeEnd}
            minLabel={`${FILTER_BEDROOMS.MIN} sovrum`}
            maxLabel={`${FILTER_BEDROOMS.MAX} sovrum`}
          />
        </AccordionTabItem>
      )}

      <AccordionTabItem
        title="Boyta"
        subtitle={`(${getLivingSpace(
          filters?.living_space_min || FILTER_LIVING_SPACE.MIN
        )} - ${getLivingSpace(
          filters?.living_space_max || FILTER_LIVING_SPACE.MAX
        )}${!filters?.living_space_max ? ' eller mer' : ''})`}
      >
        <RangeFilter
          title="living_space"
          min={FILTER_LIVING_SPACE.MIN}
          max={FILTER_LIVING_SPACE.MAX}
          step={FILTER_LIVING_SPACE.STEP}
          value={[
            filters?.living_space_min || FILTER_LIVING_SPACE.MIN,
            filters?.living_space_max || FILTER_LIVING_SPACE.MAX,
          ]}
          onChange={(value) =>
            performSearch({
              filters: {
                living_space_min:
                  value[0] <= FILTER_LIVING_SPACE.MIN ? null : value[0],
                living_space_max:
                  value[1] >= FILTER_LIVING_SPACE.MAX ? null : value[1],
              },
              ignoreSearch: true,
            })
          }
          onChangeEnd={handleChangeEnd}
          minLabel={getLivingSpace(
            filters?.living_space_min || FILTER_LIVING_SPACE.MIN
          )}
          maxLabel={getLivingSpace(
            filters?.living_space_max || FILTER_LIVING_SPACE.MAX
          )}
        />
      </AccordionTabItem>

      {(!categories || !categories?.includes('apartment')) && (
        <AccordionTabItem
          title="Tomtstorlek"
          subtitle={`(${getPlotSize(
            filters?.plot_size_min || FILTER_PLOT_SIZE.MIN,
            false
          )} - ${getPlotSize(
            filters?.plot_size_max || FILTER_PLOT_SIZE.MAX,
            false
          )}${!filters?.plot_size_max ? ' eller mer' : ''})`}
        >
          <RangeFilter
            title="plot_size"
            min={FILTER_PLOT_SIZE.MIN}
            max={FILTER_PLOT_SIZE.MAX}
            step={FILTER_PLOT_SIZE.STEP}
            value={[
              filters?.plot_size_min || FILTER_PLOT_SIZE.MIN,
              filters?.plot_size_max || FILTER_PLOT_SIZE.MAX,
            ]}
            onChange={(value) =>
              performSearch({
                filters: {
                  plot_size_min:
                    value[0] <= FILTER_PLOT_SIZE.MIN ? null : value[0],
                  plot_size_max:
                    value[1] >= FILTER_PLOT_SIZE.MAX ? null : value[1],
                },
                ignoreSearch: true,
              })
            }
            onChangeEnd={handleChangeEnd}
            minLabel={getPlotSize(
              filters?.plot_size_min || FILTER_PLOT_SIZE.MIN
            )}
            maxLabel={getPlotSize(
              filters?.plot_size_max || FILTER_PLOT_SIZE.MAX
            )}
          />
        </AccordionTabItem>
      )}

      {!hideBuildYear && (
        <AccordionTabItem
          title="Byggår"
          subtitle={`(${!filters?.build_year_min ? 'före ' : ''}${getBuildYear(
            filters?.build_year_min || FILTER_BUILD_YEAR.MIN
          )} - ${getBuildYear(
            filters?.build_year_max || FILTER_BUILD_YEAR.MAX
          )}${!filters?.build_year_max ? ' eller mer' : ''})`}
        >
          <RangeFilter
            title="build_year"
            min={FILTER_BUILD_YEAR.MIN}
            max={FILTER_BUILD_YEAR.MAX}
            step={FILTER_BUILD_YEAR.STEP}
            value={[
              filters?.build_year_min || FILTER_BUILD_YEAR.MIN,
              filters?.build_year_max || FILTER_BUILD_YEAR.MAX,
            ]}
            onChange={(value) =>
              performSearch({
                filters: {
                  build_year_min:
                    value[0] <= FILTER_BUILD_YEAR.MIN ? null : value[0],
                  build_year_max:
                    value[1] >= FILTER_BUILD_YEAR.MAX ? null : value[1],
                },
                ignoreSearch: true,
              })
            }
            onChangeEnd={handleChangeEnd}
          />
        </AccordionTabItem>
      )}

      {!hideMonthlyFee &&
        (!categories || categories?.includes('apartment')) && (
          <AccordionTabItem
            title="Månadsavgift"
            subtitle={`(${formatCurrency(
              filters?.monthly_fee_min || FILTER_MONTHLY_FEE.MIN
            )} - ${formatCurrency(
              filters?.monthly_fee_max || FILTER_MONTHLY_FEE.MAX
            )}${!filters?.monthly_fee_max ? ' +' : ''})`}
          >
            <RangeFilter
              title="monthly_fee"
              min={FILTER_MONTHLY_FEE.MIN}
              max={FILTER_MONTHLY_FEE.MAX}
              step={FILTER_MONTHLY_FEE.STEP}
              value={[
                filters?.monthly_fee_min || FILTER_MONTHLY_FEE.MIN,
                filters?.monthly_fee_max || FILTER_MONTHLY_FEE.MAX,
              ]}
              onChange={(value) =>
                performSearch({
                  filters: {
                    monthly_fee_min:
                      value[0] <= FILTER_MONTHLY_FEE.MIN ? null : value[0],
                    monthly_fee_max:
                      value[1] >= FILTER_MONTHLY_FEE.MAX ? null : value[1],
                  },
                  ignoreSearch: true,
                })
              }
              onChangeEnd={handleChangeEnd}
              minLabel={formatCurrency(
                filters?.monthly_fee_min || FILTER_MONTHLY_FEE.MIN
              )}
              maxLabel={formatCurrency(
                filters?.monthly_fee_max || FILTER_MONTHLY_FEE.MAX
              )}
            />
          </AccordionTabItem>
        )}

      {!hideNearby && (
        <AccordionTabItem
          title="Närområde"
          subtitle={getNearby()}
          panelProps={{ py: '2' }}
        >
          <Nearby ignoreSearch={ignoreSearch} />
        </AccordionTabItem>
      )}

      {!hideFeatures && (
        <AccordionTabItem
          title="Fördelar"
          subtitle={getFeatures()}
          panelProps={{ py: '2' }}
        >
          <Features ignoreSearch={ignoreSearch} />
        </AccordionTabItem>
      )}

      {!hideStatuses && (
        <AccordionTabItem
          title="Status"
          subtitle={getStatuses()}
          panelProps={{ py: '2' }}
        >
          <Statuses
            signedIn={user ? true : false}
            ignoreSearch={ignoreSearch}
            hideTitle
          />
        </AccordionTabItem>
      )}
    </Accordion>
  );
}
